import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import {
    ModalRef,
    ModalService,
    NotificationFacade,
    RelatedCardFacade,
    RelatedCardUserActions
} from '@ridango/components';
import { AppStartupService, CardGroup, RelatedCard, BaseComponent } from '@ridango/core';
import { adult } from '@ridango/core/lib/data/adult-group.data';

import { Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
@Component({
    selector: 'rwidget-related-card-detail',
    templateUrl: './related-card-detail.component.html',
    styleUrls: ['./related-card-detail.component.scss']
})
export class RelatedCardDetailComponent extends BaseComponent implements OnInit, OnDestroy {
    private blockConfirmationModal: ModalRef;

    @ViewChild('modal') modalEl; // to focus on opened modal
    @ViewChild('actionSelector') actionSelectorEl; // to focus when modal is closed
    @ViewChild('blockConfirmation', { read: TemplateRef, static: true }) blockConfirmationRef: TemplateRef<any>;

    relatedCardsLoaded$: Observable<boolean> = this.relatedCardFacade.loaded$;
    selectedRelatedCard$: Observable<RelatedCard> = this.relatedCardFacade.selectedCard$;
    actions$: Observable<RelatedCardUserActions[]> = this.relatedCardFacade.selectedCardUserActions$;
    isBlockingFinished$: Observable<boolean> = this.relatedCardFacade.isBlockingFinished$;
    isUnBlockingFinished$: Observable<boolean> = this.relatedCardFacade.isUnBlockingFinished$;
    error$: Observable<any> = this.relatedCardFacade.error$;
    allDiscounts$: Observable<CardGroup[]> = this.relatedCardFacade.allDiscountGroups$;

    cardId: number;
    cardGroups: CardGroup[];
    discount: string;

    private deleteConfirmationDialog: ModalRef;

    // TODO: review those
    actionsDropdownOpened = false;
    actionsDropdownOpenedMobile = false;

    locale = this.translate.currentLang;
    isMobileView = false;

    blockCardWithForm = this.env.RELATED_CARD.BLOCK_WITH_FORM;
    replaceCardConfirmed = true;
    replaceCardForm: FormGroup;

    @Output() onAddMoneyRequest = new EventEmitter();
    @Output() navigateToCardList = new EventEmitter();
    @Output() onActionSelected = new EventEmitter();

    @ViewChild('actionSelector') actionsEl;
    @ViewChild('actionSelectorMobile') mobileActionsEl;

    @ViewChild('deleteDialogFocus') deleteDialogFocusEl: ElementRef;
    @ViewChild('deleteConfirmation', { read: TemplateRef, static: true }) deleteConfirmationRef: TemplateRef<any>;

    constructor(
        @Inject('ENV') public env: Env,
        private relatedCardFacade: RelatedCardFacade,
        private translate: TranslateService,
        private modalService: ModalService,
        public route: ActivatedRoute,
        private notification: NotificationFacade,
        public appStartupService: AppStartupService
    ) {
        super();
    }

    ngOnInit() {
        this.route.params.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            const id = params['id'];
            this.cardId = id;
        });

        this.replaceCardForm = new FormGroup({
            first_name: new FormControl('', [Validators.required]),
            last_name: new FormControl('', [Validators.required]),
            adr_street: new FormControl('', [Validators.required]),
            adr_zip: new FormControl('', [Validators.pattern(this.env.POSTAL_CODE_PATTERN), Validators.required]),
            adr_city: new FormControl('', [Validators.required]),
            send_new: new FormControl()
        });

        this.relatedCardFacade.resetRelatedCardStatus();

        this.isBlockingFinished$.pipe(takeUntil(this.componentDestroyed$)).subscribe((isDone) => {
            if (isDone) {
                this.notification.removeAll();
                this.error$.pipe(take(1)).subscribe((e) => {
                    if (e) {
                        this.notification.error(this.translate.instant(e.error));
                        this.blockConfirmationModal.close();
                        this.actionSelectorEl.menuButton.nativeElement.focus();
                    } else {
                        this.blockConfirmationModal.close();
                        this.notification.success(this.translate.instant('LOCAL_RELATED_CARD_BLOCKED_SUCCESS'), {
                            showAfterRouteChange: true
                        });
                        this.navigateToCardList.emit();
                    }
                });
            }
        });

        this.isUnBlockingFinished$.pipe(takeUntil(this.componentDestroyed$)).subscribe((isDone) => {
            if (isDone) {
                this.notification.removeAll();
                this.error$.pipe(take(1)).subscribe((e) => {
                    if (e) {
                        // unblocking failed, show error
                        this.notification.error(this.translate.instant(e.error));
                        this.actionSelectorEl.menuButton.nativeElement.focus();
                    } else {
                        // unblock success
                        this.notification.success(this.translate.instant('LOCAL_RELATED_CARD_UNBLOCKED_SUCCESS'));
                        this.relatedCardFacade.fetchAllRelatedCards();
                        this.actionSelectorEl.menuButton.nativeElement.focus();
                    }
                });
            }
        });

        this.selectedRelatedCard$.pipe(takeUntil(this.componentDestroyed$)).subscribe((card) => {
            if (card) {
                this.allDiscounts$.pipe(takeUntil(this.componentDestroyed$)).subscribe((discounts) => {
                    if (discounts.length > 0) {
                        this.cardGroups = discounts;
                        this.findDiscountGroup(card);
                    } else {
                        this.relatedCardFacade.fetchCardsDiscountGroups();
                    }
                });
            }
        });
    }

    dropdownStateChange(isOpened: boolean) {
        // component notifies, what it wants to do. no override necessary right now
        this.actionsDropdownOpened = isOpened;
    }

    actionClicked(action: string) {
        this.onActionSelected.emit(action);
        switch (action) {
            case RelatedCardUserActions.ADD_MONEY:
                this.onAddMoneyRequest.emit();
                break;
            case RelatedCardUserActions.REMOVE:
                this.deleteConfirmationDialog = this.modalService.open(this.deleteConfirmationRef);
                setTimeout(() => this.deleteDialogFocusEl.nativeElement.focus());
                break;
            case RelatedCardUserActions.EDIT_DATA:
                break;
            case RelatedCardUserActions.FAVORITE_JOURNEY:
                break;
            case RelatedCardUserActions.BLOCK:
                this.blockConfirmationModal = this.modalService.open(this.blockConfirmationRef);
                setTimeout(() => this.modalEl.firstFocus.nativeElement.focus());
                break;
            case RelatedCardUserActions.UNBLOCK:
                this.selectedRelatedCard$.pipe(take(1)).subscribe((card) => {
                    this.relatedCardFacade.unBlockRelatedCard(card);
                    this.actionsDropdownOpened = false;
                });
                break;
        }
    }

    handleError(e: any) {
        if (e.error.description) {
            this.notification.error(this.translate.instant(e.error.description));
        } else {
            this.notification.error(this.translate.instant(e.error));
        }
    }

    setView() {
        if (window.innerWidth <= 1030) {
            this.isMobileView = true;
        }
    }

    confirmDelete() {
        this.deleteConfirmationDialog.close();
        this.relatedCardFacade.delateRelatedCard(this.cardId);

        this.relatedCardFacade.isActionCompleted$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((isActionCompleted) => {
                if (isActionCompleted) {
                    this.relatedCardFacade.error$.pipe(take(1)).subscribe((error) => {
                        if (error) {
                            this.handleError(error);
                        } else {
                            this.notification.success(this.translate.instant('LOCAL_RELATED_CARD_REMOVED_SUCCESS'), {
                                showAfterRouteChange: true
                            });
                            this.navigateToCardList.emit();
                        }
                    });
                }
            });
    }

    cancelDelete() {
        this.deleteConfirmationDialog.close();
        if (this.isMobileView) {
            setTimeout(() => this.mobileActionsEl.menuButton.nativeElement.focus());
        } else {
            this.actionsEl.menuButton.nativeElement.focus();
        }
    }

    blockCardConfirmed() {
        this.selectedRelatedCard$.pipe(take(1)).subscribe((card) => {
            if (this.blockCardWithForm && this.replaceCardConfirmed) {
                this.replaceCardForm.controls['send_new'].setValue(true);

                const { blocking_allowed: _blocking_allowed, ...strippedCard } = card;

                this.relatedCardFacade.blockAndReplaceRelatedCard(strippedCard, this.replaceCardForm.value);
            } else {
                this.relatedCardFacade.blockRelatedCard(card);
            }
        });
    }

    blockCardCanceled() {
        this.blockConfirmationModal.close();
        this.actionSelectorEl.menuButton.nativeElement.focus();
        this.replaceCardConfirmed = true;
        this.replaceCardForm.enable();
        this.replaceCardForm.reset();
    }

    toggleReplaceCardCheckbox() {
        this.replaceCardConfirmed = !this.replaceCardConfirmed;

        if (this.replaceCardConfirmed) {
            this.replaceCardForm.enable();
            this.replaceCardForm.updateValueAndValidity();
        } else {
            this.replaceCardForm.disable();
        }
    }

    findDiscountGroup(card) {
        let discountGroup = adult;

        if (card.groups && card.groups.length > 0) {
            discountGroup = this.cardGroups.find((group) => {
                return card.groups === '' + group.group_id;
            });
        }

        const discountTranslation = discountGroup.translations?.find((tr) => {
            return tr.lang === this.locale;
        });

        this.discount = discountTranslation?.translation;
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
